import { useEffect, useRef, useState } from "react";

/**
 * EmailListInput — controlled input backed by a raw draft string.
 *
 * Fixes a classic controlled-input trap: previous callers wired
 *   value={(list || []).join(", ")}
 *   onChange={(e) => parse & filter e.target.value → list}
 * which meant every keystroke was parsed, filtered (e.g. dropping
 * entries without "@"), and joined back — so typing a single letter
 * collapsed to an empty list and the character vanished immediately.
 *
 * This component keeps a local draft string so users can type freely
 * (spaces, commas, partial addresses). The array is derived + emitted
 * on every change (best-effort so parent state stays in sync for save),
 * but the input's displayed value stays the raw draft — so no character
 * ever gets swallowed while typing.
 *
 * Parent updates (e.g. loading an existing site into the form) resync
 * the draft when the incoming array is not the current parse of the
 * draft — so we don't clobber the user's mid-word typing.
 */
export function EmailListInput({
  value,
  onChange,
  multiline = false,
  rows = 2,
  className = "",
  placeholder,
  disabled = false,
  testId,
}) {
  const initialArr = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState(initialArr.join(", "));
  const parsedRef = useRef(initialArr);

  // Resync when parent value is set from outside (e.g., editing an
  // existing site swaps the form in). Compare against our last-emitted
  // parse so we don't stomp the draft while the user is typing.
  useEffect(() => {
    const incoming = Array.isArray(value) ? value : [];
    const same =
      incoming.length === parsedRef.current.length &&
      incoming.every((v, i) => v === parsedRef.current[i]);
    if (!same) {
      parsedRef.current = incoming;
      setDraft(incoming.join(", "));
    }
  }, [value]);

  const handleChange = (raw) => {
    setDraft(raw);
    const list = raw
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    parsedRef.current = list;
    onChange(list);
  };

  const commonProps = {
    "data-testid": testId,
    className,
    placeholder,
    disabled,
    value: draft,
    onChange: (e) => handleChange(e.target.value),
  };

  return multiline ? (
    <textarea rows={rows} {...commonProps} />
  ) : (
    <input {...commonProps} />
  );
}
